import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FinMark from '../brand/FinMark';
import { Card, Container, Section, SectionHeader } from './primitives';
import { formatBRL, richText } from './richText';
import { landing } from './tokens';

const FeatureCard: React.FC<{
  title: string;
  text: string;
  children: React.ReactNode;
}> = ({ title, text, children }) => (
  <Card sx={{ gap: 1.75, p: 3.25 }}>
    <Box component='h3' sx={{ fontSize: 21, letterSpacing: '-0.01em' }}>
      {title}
    </Box>
    <Box component='p' sx={{ color: landing.ink2, lineHeight: 1.55, fontSize: 15.5 }}>
      {text}
    </Box>
    <Box sx={{ mt: 'auto', pt: 0.5 }}>{children}</Box>
  </Card>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.25,
      px: 1.5,
      py: 1.25,
      borderRadius: '12px',
      bgcolor: landing.bg2,
      fontSize: 14,
    }}
  >
    {children}
  </Box>
);

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='span' sx={{ color: landing.ink3 }}>
    {children}
  </Box>
);

const Bar: React.FC<{ value: number; height?: number; track?: string }> = ({
  value,
  height = 8,
  track = landing.bg2,
}) => (
  <Box sx={{ height, borderRadius: 99, bgcolor: track }}>
    <Box
      sx={{
        width: `${value}%`,
        height,
        borderRadius: 99,
        bgcolor: landing.accent,
      }}
    />
  </Box>
);

const categoryRows = [
  { key: 'lunch', amount: 38 },
  { key: 'uber', amount: 23.9 },
  { key: 'pharmacy', amount: 62 },
];
const billRows = [
  { key: 'power', amount: 230, urgent: true },
  { key: 'laptop', amount: 420, urgent: false },
  { key: 'rent', amount: 1240, urgent: false },
];
const currencyRows = [
  { key: 'dinner', amount: 247.5 },
  { key: 'museum', amount: 106.2 },
];

const EverydayFeatures: React.FC = () => {
  const { t, i18n } = useTranslation('landing');
  const money = (value: number) => formatBRL(value, i18n.language);
  return (
    <Section id='recursos'>
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SectionHeader
          eyebrow={t('everyday.eyebrow')}
          title={t('everyday.title')}
          lead={t('everyday.description')}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(330px, 100%), 1fr))',
            gap: 2.5,
          }}
        >
          <FeatureCard
            title={t('everyday.categories.title')}
            text={t('everyday.categories.text')}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {categoryRows.map((row) => (
                <Row key={row.key}>
                  <span>
                    {t(`everyday.categories.${row.key}.name`)}{' '}
                    <Muted>· {t(`everyday.categories.${row.key}.method`)}</Muted>
                  </span>
                  <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component='span'
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 99,
                        bgcolor: landing.accentSoft2,
                        color: landing.accent,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {t(`everyday.categories.${row.key}.category`)}
                    </Box>
                    <strong>{money(row.amount)}</strong>
                  </Box>
                </Row>
              ))}
            </Box>
          </FeatureCard>

          <FeatureCard title={t('everyday.bills.title')} text={t('everyday.bills.text')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {billRows.map((row) => (
                <Row key={row.key}>
                  <span>{t(`everyday.bills.${row.key}.name`)}</span>
                  <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component='span'
                      sx={{
                        fontSize: 12,
                        fontWeight: row.urgent ? 700 : 400,
                        color: row.urgent ? landing.warn : landing.ink3,
                      }}
                    >
                      {t(`everyday.bills.${row.key}.due`)}
                    </Box>
                    <strong>{money(row.amount)}</strong>
                  </Box>
                </Row>
              ))}
            </Box>
          </FeatureCard>

          <FeatureCard title={t('everyday.goals.title')} text={t('everyday.goals.text')}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                p: 2,
                borderRadius: '16px',
                bgcolor: landing.bg2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box component='strong' sx={{ fontSize: 15 }}>
                  {t('everyday.goals.name')}
                </Box>
                <Box component='span' sx={{ fontSize: 13, color: landing.ink3 }}>
                  57%
                </Box>
              </Box>
              <Bar value={57} height={10} track={landing.line} />
              <Box component='span' sx={{ fontSize: 14, color: landing.ink2 }}>
                {t('everyday.goals.progress', { saved: money(2280), target: money(4000) })}
              </Box>
            </Box>
          </FeatureCard>

          <FeatureCard title={t('everyday.budget.title')} text={t('everyday.budget.text')}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                p: 2,
                borderRadius: '16px',
                bgcolor: landing.bg2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box component='strong' sx={{ fontSize: 15 }}>
                  {t('everyday.budget.month')}
                </Box>
                <Box component='span' sx={{ fontSize: 13, color: landing.ink3 }}>
                  69%
                </Box>
              </Box>
              <Bar value={69} height={10} track={landing.line} />
              <Box component='span' sx={{ fontSize: 14, color: landing.ink2 }}>
                {t('everyday.budget.progress', { spent: money(4832.1), budget: money(7000) })}
              </Box>
              <Box component='span' sx={{ fontSize: 13, color: landing.ink3 }}>
                {t('everyday.budget.pace', { total: money(5990) })}
              </Box>
            </Box>
          </FeatureCard>

          <FeatureCard title={t('everyday.summary.title')} text={t('everyday.summary.text')}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <FinMark size={32} />
              <Box
                sx={{
                  px: 1.75,
                  py: 1.4,
                  borderRadius: '18px 18px 18px 5px',
                  bgcolor: landing.accentSoft,
                  border: `1px solid ${landing.accentSoft2}`,
                  fontSize: 14.5,
                  lineHeight: 1.5,
                }}
              >
                {richText(t('everyday.summary.message'))}
              </Box>
            </Box>
          </FeatureCard>

          <FeatureCard title={t('everyday.currency.title')} text={t('everyday.currency.text')}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {currencyRows.map((row) => (
                <Row key={row.key}>
                  <span>
                    {t(`everyday.currency.${row.key}.name`)}{' '}
                    <Muted>· {t(`everyday.currency.${row.key}.original`)}</Muted>
                  </span>
                  <strong>{money(row.amount)}</strong>
                </Row>
              ))}
            </Box>
          </FeatureCard>
        </Box>
      </Container>
    </Section>
  );
};

export default EverydayFeatures;
